import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Visibility, EditRounded, CheckRounded } from '@mui/icons-material';

import { Campaign } from '@libs/models';
import { CampaignPartner, CampaignPPC, CampaignPPCOptions, CampaignStatus } from '@libs/types';
import { DashboardPageContainer, DataGridV2 as DataGrid, Drawer, CurrencyInput, CampaignPartnerChip } from '@carcareers/components';
import { CAMPAIGN_INDEX_COLUMNS, CAMPAIGN_INDEX_COLUMN_INITIAL_STATE, routes } from '@carcareers/utils';
import { useCampaignSearchQuery } from '@carcareers/store';
import { useCampaign } from '@carcareers/hooks';
import { useDrawer } from '@carcareers/contexts';

export const CampaignIndex: FC = () => {
  const navigate = useNavigate();
  const searchCampaigns = useCampaignSearchQuery;
  const { updateCampaign } = useCampaign();
  const { setOpen } = useDrawer();
  const [activeCampaign, setActiveCampaign] = useState<Campaign>();
  const [activeBudget, setActiveBudget] = useState<number>(0);

  const handleUpdateStatus = useCallback(
    (id?: number, status?: CampaignStatus) => async () => {
      if (!id) return;
      await updateCampaign({ id, status }, { onSuccess: () => setOpen(false) });
    },
    [updateCampaign],
  );

  const handleUpdatePPC = useCallback(
    async (id?: number, value?: { label: string; value: CampaignPPC }) => {
      await updateCampaign({ id, ppc: value?.value }, { onSuccess: () => setOpen(false) });
    },
    [updateCampaign],
  );

  const handleUpdateBudget = useCallback(
    (id?: number, value?: number) => async () => {
      if (!id) return;
      const budget = value ? parseFloat(value.toString()) : undefined;
      await updateCampaign({ id, budget }, { onSuccess: () => setOpen(false) });
    },
    [updateCampaign],
  );

  const columns = useMemo(
    () => [
      ...CAMPAIGN_INDEX_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as const,
        flex: 1,
        align: 'right' as const,
        headerAlign: 'right' as const,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            showInMenu
            key={`view-${params.row.id}`}
            label="View"
            icon={<Visibility />}
            onClick={() =>
              navigate(`/${routes.ADMIN_HOME}/${routes.ADMIN_CAMPAIGNS_VIEW.replace(':id', params.row.id.toString())}`)
            }
          />,
          ...(params?.row?.partner !== CampaignPartner.APPCAST
            ? [
                <GridActionsCellItem
                  showInMenu
                  key={`update-${params.row.id}`}
                  label="Quick Edit"
                  icon={<EditRounded />}
                  onClick={() => {
                    setOpen(true);
                    setActiveCampaign(params.row);
                    setActiveBudget(params.row.budget ?? 0);
                  }}
                />,
              ]
            : []),
        ],
      },
    ],
    [handleUpdateStatus, navigate],
  );

  const statusButtonSx = {
    height: 56,
    borderRadius: 3,
  };

  return (
    <>
      <DashboardPageContainer>
        <DataGrid<Campaign>
          query={searchCampaigns}
          columns={columns}
          initialState={{
            columns: {
              columnVisibilityModel: CAMPAIGN_INDEX_COLUMN_INITIAL_STATE,
            },
          }}
        />
      </DashboardPageContainer>
      <Drawer title={`${activeCampaign?.id} - ${activeCampaign?.post?.job?.title}`} width={500}>
        <Box sx={{ px: 2, py: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CampaignPartnerChip partner={activeCampaign?.partner} />
          <Typography variant="h4" sx={{ mt: 2 }}>Price Per Click</Typography>
          <Select
            name="ppc"
            value={activeCampaign?.ppc ?? 'Pinto'}
            onChange={(e) => {
              handleUpdatePPC(activeCampaign?.id, { label: e.target.value, value: e.target.value as CampaignPPC });
            }}
            sx={{
              borderRadius: 3,
            }}
          >
            {CampaignPPCOptions.map((option) => (
              <MenuItem key={`ppc-${option.value}`} value={option.value} sx={{ color: 'text.primary' }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="h4">Budget</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <CurrencyInput
              fullWidth
              value={activeBudget}
              onChange={(value) => {
                setActiveBudget(value ?? 0);
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateBudget(activeCampaign?.id, activeBudget)}
              sx={{
                width: 56,
                height: 56,
                minWidth: 56,
                borderRadius: 3,
              }}
            >
              <CheckRounded sx={{ strokeWidth: 2, stroke: 'white' }} />
            </Button>
          </Box>
          <Typography variant="h4">Status</Typography>
          <Button
            variant={activeCampaign?.status === CampaignStatus.ACTIVE ? 'contained' : 'outlined'}
            color="primary"
            onClick={handleUpdateStatus(activeCampaign?.id, CampaignStatus.ACTIVE)}
            sx={statusButtonSx}
          >
            <Typography
              variant="h5"
              sx={{ color: activeCampaign?.status === CampaignStatus.ACTIVE ? 'primary.contrastText' : 'primary.main' }}
            >
              {activeCampaign?.status === CampaignStatus.ACTIVE ? 'Active' : 'Activate'}
            </Typography>
          </Button>
          <Button
            variant={activeCampaign?.status === CampaignStatus.PAUSED ? 'contained' : 'outlined'}
            color="primary"
            onClick={handleUpdateStatus(activeCampaign?.id, CampaignStatus.PAUSED)}
            sx={statusButtonSx}
          >
            <Typography
              variant="h5"
              sx={{ color: activeCampaign?.status === CampaignStatus.PAUSED ? 'primary.contrastText' : 'primary.main' }}
            >
              {activeCampaign?.status === CampaignStatus.PAUSED ? 'Paused' : 'Pause'}
            </Typography>
          </Button>
          <Button
            variant={activeCampaign?.status === CampaignStatus.COMPLETED ? 'contained' : 'outlined'}
            color="primary"
            onClick={handleUpdateStatus(activeCampaign?.id, CampaignStatus.COMPLETED)}
            sx={statusButtonSx}
          >
            <Typography
              variant="h5"
              sx={{
                color: activeCampaign?.status === CampaignStatus.COMPLETED ? 'primary.contrastText' : 'primary.main',
              }}
            >
              {activeCampaign?.status === CampaignStatus.COMPLETED ? 'Completed' : 'Complete'}
            </Typography>
          </Button>
          <Button
            variant={activeCampaign?.status === CampaignStatus.CANCELLED ? 'contained' : 'outlined'}
            color="primary"
            onClick={handleUpdateStatus(activeCampaign?.id, CampaignStatus.CANCELLED)}
            sx={statusButtonSx}
          >
            <Typography
              variant="h5"
              sx={{
                color: activeCampaign?.status === CampaignStatus.CANCELLED ? 'primary.contrastText' : 'primary.main',
              }}
            >
              {activeCampaign?.status === CampaignStatus.CANCELLED ? 'Cancelled' : 'Cancel'}
            </Typography>
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default CampaignIndex;
